import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, loadsAPI } from '../services/api';
import Button from '../components/Button';
import LoadDetailViewModal from '../components/LoadDetailViewModal';
import LoadDatePicker from '../components/LoadDatePicker';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { formatDateKey, formatWeight, openPdfInNewTab } from '../utils/helpers';
import { getLoadStatusBadgeColor } from '../components/LoadDetailViewModal';

const ReportsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { error: toastError } = useToast();
  const isPeseur = user?.role === 'PESEUR';
  const peseurDisplayName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || '';
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [dateRange, setDateRange] = useState(null);
  const [reportPeseurs, setReportPeseurs] = useState([]);
  const [rapportWeighbridges, setRapportWeighbridges] = useState([]);
  const [rapportPeseurs, setRapportPeseurs] = useState([]);
  const [rapportPeseursLoading, setRapportPeseursLoading] = useState(false);
  const [rapportPdfLoading, setRapportPdfLoading] = useState(false);
  const [filterLoadDates, setFilterLoadDates] = useState([]);
  const [rapportLoadDates, setRapportLoadDates] = useState([]);
  const [filterDatesLoading, setFilterDatesLoading] = useState(false);
  const [rapportDatesLoading, setRapportDatesLoading] = useState(false);
  const [rapportForm, setRapportForm] = useState({
    date: '',
    weighbridgeId: '',
    peseurId: ''
  });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    peseurId: '',
    page: 1,
    limit: 100
  });
  const [detailRow, setDetailRow] = useState(null);
  const [productLoadsOpen, setProductLoadsOpen] = useState(false);
  const [productLoadsTarget, setProductLoadsTarget] = useState(null);
  const [productLoads, setProductLoads] = useState([]);
  const [productLoadsLoading, setProductLoadsLoading] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedLoadLoading, setSelectedLoadLoading] = useState(false);

  const rowGroupKey = (row) => `${row.date}-${row.weighbridgeId}-${row.peseurId}`;

  const openProductLoads = async (product) => {
    if (!detailRow || !product?.numLoads) return;
    setProductLoadsTarget(product);
    setProductLoadsOpen(true);
    setProductLoads([]);
    setProductLoadsLoading(true);
    try {
      const response = await reportsAPI.getReportDayLoads({
        date: detailRow.date,
        weighbridgeId: detailRow.weighbridgeId,
        peseurId: detailRow.peseurId,
        productId: product.productId
      });
      setProductLoads(response.data?.loads || []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('reports.productLoadsError');
      toastError(msg);
      setProductLoadsOpen(false);
    } finally {
      setProductLoadsLoading(false);
    }
  };

  const openLoadDetail = async (loadId) => {
    if (!loadId) return;
    setSelectedLoadLoading(true);
    try {
      const response = await loadsAPI.getById(loadId);
      setSelectedLoad(response.data?.load || response.data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('reports.loadDetailError');
      toastError(msg);
    } finally {
      setSelectedLoadLoading(false);
    }
  };

  const renderLoadCountLink = (count, product) => {
    const n = Number(count) || 0;
    if (n <= 0) {
      return <span className="text-gray-400">0</span>;
    }
    return (
      <button
        type="button"
        onClick={() => openProductLoads(product)}
        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
      >
        {n.toLocaleString()}
      </button>
    );
  };

  const fetchDailyTotalsByWeighbridgeProduct = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.peseurId) params.peseurId = filters.peseurId;
      params.page = filters.page;
      params.limit = filters.limit;

      const response = await reportsAPI.getDailyTotalsByWeighbridgeProduct(params);
      const payload = response.data || {};
      setData(Array.isArray(payload.data) ? payload.data : []);
      setSummary(payload.summary || {});
      setPagination(payload.pagination || {});
      setDateRange(payload.dateRange || null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch reports data';
      setError(errorMessage);
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPeseur && user?.id) {
      setFilters((prev) => ({ ...prev, peseurId: user.id }));
      setRapportForm((prev) => ({ ...prev, peseurId: user.id }));
    }
  }, [isPeseur, user?.id]);

  useEffect(() => {
    if (!isPeseur) {
      reportsAPI
        .getReportPeseurs()
        .then((res) => setReportPeseurs(res.data.peseurs || []))
        .catch((err) => {
          console.error('Error fetching report peseurs:', err);
          setReportPeseurs([]);
        });
    }
    reportsAPI
      .getRapportJournalierWeighbridges()
      .then((res) => setRapportWeighbridges(res.data.weighbridges || []))
      .catch((err) => console.error('Error fetching rapport weighbridges:', err));
  }, [isPeseur]);

  useEffect(() => {
    setFilterDatesLoading(true);
    const params = {};
    if (filters.peseurId) {
      params.peseurId = filters.peseurId;
    }
    reportsAPI
      .getLoadDates(params)
      .then((res) => {
        const dates = res.data?.dates || [];
        setFilterLoadDates(dates);
        setFilters((prev) => {
          const next = { ...prev };
          if (prev.startDate && dates.length > 0 && !dates.includes(prev.startDate)) {
            next.startDate = '';
          }
          if (prev.endDate && dates.length > 0 && !dates.includes(prev.endDate)) {
            next.endDate = '';
          }
          return next;
        });
      })
      .catch((err) => {
        console.error('Error fetching report filter dates:', err);
        setFilterLoadDates([]);
      })
      .finally(() => setFilterDatesLoading(false));
  }, [filters.peseurId]);

  useEffect(() => {
    if (!rapportForm.weighbridgeId) {
      setRapportLoadDates([]);
      setRapportForm((prev) => ({ ...prev, date: '' }));
      return;
    }
    const peseurIdForDates = isPeseur && user?.id ? user.id : rapportForm.peseurId;
    if (!peseurIdForDates) {
      setRapportLoadDates([]);
      setRapportForm((prev) => ({ ...prev, date: '' }));
      return;
    }
    setRapportDatesLoading(true);
    reportsAPI
      .getLoadDates({
        weighbridgeId: rapportForm.weighbridgeId,
        peseurId: peseurIdForDates
      })
      .then((res) => {
        const dates = res.data?.dates || [];
        setRapportLoadDates(dates);
        setRapportForm((prev) => {
          if (dates.length === 0) {
            return { ...prev, date: '' };
          }
          if (prev.date && dates.includes(prev.date)) {
            return prev;
          }
          return { ...prev, date: dates[dates.length - 1] };
        });
      })
      .catch((err) => {
        console.error('Error fetching rapport journalier dates:', err);
        setRapportLoadDates([]);
      })
      .finally(() => setRapportDatesLoading(false));
  }, [rapportForm.weighbridgeId, rapportForm.peseurId, isPeseur, user?.id]);

  useEffect(() => {
    if (!rapportForm.weighbridgeId) {
      setRapportPeseurs([]);
      return;
    }
    if (isPeseur && user?.id) {
      setRapportForm((prev) => ({ ...prev, peseurId: user.id }));
      setRapportPeseurs([
        {
          id: user.id,
          name: peseurDisplayName
        }
      ]);
      return;
    }
    setRapportPeseursLoading(true);
    reportsAPI
      .getRapportJournalierPeseurs(rapportForm.weighbridgeId)
      .then((res) => setRapportPeseurs(res.data.peseurs || []))
      .catch((err) => {
        console.error('Error fetching rapport peseurs:', err);
        setRapportPeseurs([]);
      })
      .finally(() => setRapportPeseursLoading(false));
  }, [rapportForm.weighbridgeId, isPeseur, user?.id, peseurDisplayName]);

  useEffect(() => {
    fetchDailyTotalsByWeighbridgeProduct();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps -- refresh report data whenever filters change

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handlePrintRapportJournalier = async () => {
    if (!rapportForm.date || !rapportForm.weighbridgeId || !rapportForm.peseurId) {
      toastError(t('reports.rapportJournalier.validation'));
      return;
    }
    try {
      setRapportPdfLoading(true);
      const lang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
      const response = await reportsAPI.getRapportJournalierPDF({
        date: rapportForm.date,
        weighbridgeId: rapportForm.weighbridgeId,
        peseurId: rapportForm.peseurId,
        lang
      });
      const filename = `rapport-journalier-${rapportForm.date}.pdf`;
      const opened = await openPdfInNewTab(response.data, filename);
      if (!opened) {
        toastError(t('reports.rapportJournalier.popupBlocked'));
        return;
      }
    } catch (err) {
      let msg = err.message || t('reports.rapportJournalier.pdfError');
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (
            parsed.code === 'REPORTS.RAPPORT_NO_LOADS' ||
            parsed.code === 'REPORTS.RAPPORT_NO_CERTIFIED_LOADS'
          ) {
            msg = t('reports.rapportJournalier.noLoads');
          } else if (parsed.message) {
            msg = parsed.message;
          }
        } catch {
          /* keep default */
        }
      } else if (
        data?.code === 'REPORTS.RAPPORT_NO_LOADS' ||
        data?.code === 'REPORTS.RAPPORT_NO_CERTIFIED_LOADS'
      ) {
        msg = t('reports.rapportJournalier.noLoads');
      } else if (data?.message) {
        msg = data.message;
      }
      toastError(msg);
    } finally {
      setRapportPdfLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      t('reports.date'),
      t('reports.weighbridge'),
      t('reports.peseur'),
      t('reports.product'),
      t('reports.numberOfLoads'),
      t('reports.numberOfCertificates'),
      `${t('reports.totalWeightColumn')} (Ton)`,
      t('reports.totalAmountPaid')
    ];
    const lines = [headers.join(',')];
    data.forEach((row) => {
      const products = row.products?.length ? row.products : [];
      if (products.length === 0) {
        lines.push(
          [
            row.date,
            `"${row.weighbridgeName}"`,
            `"${row.peseurName}"`,
            '""',
            row.numLoads,
            row.numCertificates,
            ((Number(row.totalWeight) || 0) / 1000).toFixed(2),
            row.totalAmountPaid.toFixed(2)
          ].join(',')
        );
      } else {
        products.forEach((p) => {
          lines.push(
            [
              row.date,
              `"${row.weighbridgeName}"`,
              `"${row.peseurName}"`,
              `"${p.productName}"`,
              p.numLoads,
              p.numCertificates,
              ((Number(p.totalWeight) || 0) / 1000).toFixed(2),
              p.totalAmountPaid.toFixed(2)
            ].join(',')
          );
        });
      }
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-loads-by-weighbridge-peseur-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <ChartBarIcon className="h-8 w-8 text-blue-600" />
        <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('reports.dailyLoadsByWeighbridge')}</h1>
            <p className="text-gray-600">
              {isPeseur ? t('reports.dailyLoadsByWeighbridgeDescPeseur') : t('reports.dailyLoadsByWeighbridgeDesc')}
            </p>
          </div>
        </div>
        
        <button
          onClick={exportToCSV}
          disabled={data.length === 0}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
          {t('reports.exportCSV')}
        </button>
      </div>

      <div className="card border border-blue-100 bg-blue-50/30">
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {t('reports.rapportJournalier.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{t('reports.rapportJournalier.description')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('reports.rapportJournalier.weighbridge')}
            </label>
            <select
              value={rapportForm.weighbridgeId}
              onChange={(e) =>
                setRapportForm((prev) => ({
                  ...prev,
                  weighbridgeId: e.target.value,
                  peseurId: isPeseur && user?.id ? user.id : ''
                }))
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('reports.rapportJournalier.selectWeighbridge')}</option>
              {rapportWeighbridges.map((wb) => (
                <option key={wb.id} value={wb.id}>
                  {wb.name}
                  {wb.zoneLabel ? ` — ${wb.zoneLabel}` : ''}
                </option>
              ))}
            </select>
            {rapportWeighbridges.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                {t('reports.rapportJournalier.noWeighbridges')}
              </p>
            )}
          </div>
          {!isPeseur ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.rapportJournalier.peseur')}
              </label>
              <select
                value={rapportForm.peseurId}
                onChange={(e) =>
                  setRapportForm((prev) => ({ ...prev, peseurId: e.target.value }))
                }
                disabled={!rapportForm.weighbridgeId || rapportPeseursLoading}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="">{t('reports.rapportJournalier.selectPeseur')}</option>
                {rapportPeseurs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {rapportForm.weighbridgeId && !rapportPeseursLoading && rapportPeseurs.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  {t('reports.rapportJournalier.noPeseurs')}
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.rapportJournalier.peseur')}
              </label>
              <p className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-800">
                {peseurDisplayName}
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('reports.rapportJournalier.date')}
            </label>
            <LoadDatePicker
              value={rapportForm.date}
              onChange={(date) => setRapportForm((prev) => ({ ...prev, date }))}
              availableDates={rapportLoadDates}
              loading={rapportDatesLoading}
              disabled={!rapportForm.weighbridgeId || (!isPeseur && !rapportForm.peseurId)}
              placeholder={t('reports.rapportJournalier.selectDate')}
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            type="button"
            onClick={handlePrintRapportJournalier}
            loading={rapportPdfLoading}
            disabled={!rapportForm.date || !rapportForm.weighbridgeId || !rapportForm.peseurId}
          >
            <>
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              {rapportPdfLoading
                ? t('reports.rapportJournalier.printing')
                : t('reports.rapportJournalier.printPdf')}
            </>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">{t('reports.filters')}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('reports.startDate')}
            </label>
            <LoadDatePicker
              value={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              availableDates={filterLoadDates}
              loading={filterDatesLoading}
              allowEmpty
              placeholder={t('reports.allDates')}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('reports.endDate')}
            </label>
            <LoadDatePicker
              value={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              availableDates={filterLoadDates}
              loading={filterDatesLoading}
              allowEmpty
              placeholder={t('reports.allDates')}
            />
          </div>

          {!isPeseur && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.filterPeseurLabel')}
              </label>
              <select
                value={filters.peseurId}
                onChange={(e) => handleFilterChange('peseurId', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('reports.filterAllPeseurs')}</option>
                {reportPeseurs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName || `${p.firstName || ''} ${p.lastName || ''}`.trim()}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('reports.resultsPerPage')}
            </label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() =>
                setFilters({
                  startDate: '',
                  endDate: '',
                  peseurId: isPeseur && user?.id ? user.id : '',
                  page: 1,
                  limit: 100
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('reports.clearFilters')}
            </button>
          </div>
        </div>
        {dateRange?.startDate && dateRange?.endDate && (
          <p className="mt-3 text-sm text-gray-600">
            {dateRange.isDefaultRange
              ? t('reports.activeDateRangeDefault', {
                  start: formatDateKey(dateRange.startDate),
                  end: formatDateKey(dateRange.endDate),
                  days: dateRange.defaultDays || 365
                })
              : t('reports.activeDateRange', {
                  start: formatDateKey(dateRange.startDate),
                  end: formatDateKey(dateRange.endDate)
                })}
          </p>
        )}
      </div>
      
      {/* Summary Cards */}
      {summary && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('reports.totalLoads')}</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalLoads?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <DocumentArrowDownIcon className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('reports.totalCertificates')}</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalCertificates?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-yellow-600 font-bold text-sm">TON</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('reports.totalWeight')}</p>
                <p className="text-2xl font-bold text-gray-900">{formatWeight(summary.totalWeight || 0, 'ton', { noUnit: true })}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-sm">FCFA</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('reports.totalRevenue')}</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalAmountPaid || 0)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">{t('reports.dailyLoadsByWeighbridge')}</h3>
          {pagination.total > 0 && pagination.totalPages > 1 && (
            <p className="text-xs sm:text-sm text-gray-500">
              {t('reports.showingResults', {
                start: ((pagination.page - 1) * pagination.limit) + 1,
                end: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total
              })}
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchDailyTotalsByWeighbridgeProduct}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('reports.retry')}
            </button>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('reports.noDataFound')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('reports.tryAdjustingFilters')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-4 w-full">
              {data.map((row) => (
                <div
                  key={rowGroupKey(row)}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-2.5 hover:shadow-md transition-shadow w-full"
                >
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.date')}:
                    </div>
                    <div className="text-sm text-gray-900 break-words flex-1 min-w-0">
                      {formatDateKey(row.date)}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.weighbridge')}:
                    </div>
                    <div className={`text-sm break-words flex-1 min-w-0 ${row.weighbridgeId === 'no-weighbridge' ? 'text-gray-400 italic' : 'text-gray-900'}`}>
                      {row.weighbridgeId === 'no-weighbridge' ? t('reports.noWeighbridgeAssigned') : row.weighbridgeName}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.peseur')}:
                    </div>
                    <div className={`text-sm break-words flex-1 min-w-0 ${row.peseurId === 'no-peseur' ? 'text-gray-400 italic' : 'text-gray-900'}`}>
                      {row.peseurId === 'no-peseur' ? t('reports.noPeseurAssigned') : row.peseurName}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.numberOfProducts')}:
                    </div>
                    <div className="text-sm text-gray-900 break-words flex-1 min-w-0">
                      {(row.numProducts ?? row.products?.length ?? 0).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.numberOfLoads')}:
                    </div>
                    <div className="text-sm text-gray-900 break-words flex-1 min-w-0">
                      {row.numLoads.toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.numberOfCertificates')}:
                    </div>
                    <div className="text-sm text-gray-900 break-words flex-1 min-w-0">
                      {row.numCertificates.toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.totalWeightColumn')}:
                    </div>
                    <div className="text-sm text-gray-900 break-words flex-1 min-w-0">
                      {formatWeight(row.totalWeight, 'ton', { noUnit: true })}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 py-1 w-full min-w-0">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" style={{ minWidth: '90px', maxWidth: '90px' }}>
                      {t('reports.totalAmountPaid')}:
                    </div>
                    <div className="text-sm font-semibold text-gray-900 break-words flex-1 min-w-0">
                      {formatCurrency(row.totalAmountPaid)}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setDetailRow(row)}
                    className="mt-2 w-full inline-flex items-center justify-center px-3 py-2 border border-blue-200 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    {t('reports.viewDetails')}
                  </button>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 max-w-32">
                      <div className="break-words">
                        {t('reports.weighbridge')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.peseur')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.numberOfProducts')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.numberOfLoads')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.numberOfCertificates')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.totalWeightColumn')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.totalAmountPaid')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('reports.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((row, index) => (
                    <tr key={rowGroupKey(row)} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDateKey(row.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 w-32 max-w-32">
                        <div className={`break-words ${row.weighbridgeId === 'no-weighbridge' ? 'text-gray-400 italic' : ''}`}>
                          {row.weighbridgeId === 'no-weighbridge' ? t('reports.noWeighbridgeAssigned') : row.weighbridgeName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={row.peseurId === 'no-peseur' ? 'text-gray-400 italic' : ''}>
                          {row.peseurId === 'no-peseur' ? t('reports.noPeseurAssigned') : row.peseurName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(row.numProducts ?? row.products?.length ?? 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.numLoads.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.numCertificates.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatWeight(row.totalWeight, 'ton', { noUnit: true })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(row.totalAmountPaid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          type="button"
                          onClick={() => setDetailRow(row)}
                          className="inline-flex items-center px-3 py-1.5 border border-blue-200 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                        >
                          <EyeIcon className="h-4 w-4 mr-1.5" />
                          {t('reports.viewDetails')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                <div className="flex flex-1 justify-between sm:hidden gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex-1"
                  >
                    {t('reports.previous')}
                  </button>
                  <div className="flex items-center text-xs text-gray-700 px-2">
                    {t('reports.showingPage', {
                      page: pagination.page,
                      totalPages: pagination.totalPages
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="relative inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex-1"
                  >
                    {t('reports.next')}
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      {t('reports.showingPage', {
                        page: pagination.page,
                        totalPages: pagination.totalPages
                      })}
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('reports.previous')}
                      </button>
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, pagination.page - 2) + i;
                        if (pageNum > pagination.totalPages) return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                              pageNum === pagination.page
                                ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('reports.next')}
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={!!detailRow}
        onClose={() => setDetailRow(null)}
        title={t('reports.detailModalTitle')}
        size="xl"
      >
        {detailRow && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-gray-50 rounded-lg p-4">
              <div>
                <span className="font-medium text-gray-500">{t('reports.date')}:</span>{' '}
                <span className="text-gray-900">{formatDateKey(detailRow.date)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">{t('reports.peseur')}:</span>{' '}
                <span className="text-gray-900">
                  {detailRow.peseurId === 'no-peseur'
                    ? t('reports.noPeseurAssigned')
                    : detailRow.peseurName}
                </span>
              </div>
              <div className="sm:col-span-2">
                <span className="font-medium text-gray-500">{t('reports.weighbridge')}:</span>{' '}
                <span className="text-gray-900">
                  {detailRow.weighbridgeId === 'no-weighbridge'
                    ? t('reports.noWeighbridgeAssigned')
                    : detailRow.weighbridgeName}
                </span>
              </div>
            </div>

            <p className="text-sm font-medium text-gray-700">{t('reports.productsBreakdown')}</p>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.product')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.numberOfLoads')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.numberOfCertificates')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.totalWeightColumn')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.totalAmountPaid')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {(detailRow.products || []).map((p) => (
                    <tr key={p.productId}>
                      <td className="px-4 py-3 text-sm text-gray-900">{p.productName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {renderLoadCountLink(p.numLoads, p)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {p.numCertificates.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatWeight(p.totalWeight, 'ton', { noUnit: true })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatCurrency(p.totalAmountPaid)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-semibold text-sm">
                  <tr>
                    <td className="px-4 py-3 text-gray-900">{t('reports.totals')}</td>
                    <td className="px-4 py-3 text-gray-900">
                      {detailRow.numLoads > 0 ? (
                        <span className="text-gray-900">{detailRow.numLoads.toLocaleString()}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {detailRow.numCertificates.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {formatWeight(detailRow.totalWeight, 'ton', { noUnit: true })}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {formatCurrency(detailRow.totalAmountPaid)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {(!detailRow.products || detailRow.products.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">{t('reports.noDataFound')}</p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={productLoadsOpen}
        onClose={() => {
          setProductLoadsOpen(false);
          setProductLoadsTarget(null);
          setProductLoads([]);
        }}
        title={
          productLoadsTarget && detailRow
            ? t('reports.productLoadsTitle', {
                product: productLoadsTarget.productName,
                date: formatDateKey(detailRow.date)
              })
            : t('reports.productLoadsTitleShort')
        }
        size="xl"
      >
        {productLoadsLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : productLoads.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">{t('reports.noDataFound')}</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[60vh] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('reports.loadTicket')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('reports.loadClient')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('reports.loadTruck')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('reports.loadWeight')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('reports.loadStatus')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('reports.totalAmountPaid')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {productLoads.map((load) => (
                  <tr
                    key={load.id}
                    className="hover:bg-blue-50 cursor-pointer"
                    onClick={() => openLoadDetail(load.id)}
                  >
                    <td className="px-3 py-2 text-sm">
                      {load.ticketNumber ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLoadDetail(load.id);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          {load.ticketNumber}
                        </button>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">{load.clientName}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{load.truckPlate}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatWeight(load.netWeight, 'ton', { noUnit: true })}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getLoadStatusBadgeColor(load.status)}`}
                      >
                        {t(`loads.statuses.${String(load.status || '').toLowerCase()}`, load.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatCurrency(load.amountPaid)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-gray-500">{t('reports.productLoadsHint')}</p>
      </Modal>

      <LoadDetailViewModal
        isOpen={!!selectedLoad || selectedLoadLoading}
        onClose={() => setSelectedLoad(null)}
        load={selectedLoad}
        loading={selectedLoadLoading}
      />
    </div>
  );
};

export default ReportsPage;